import React, { useState } from 'react';
import axios from 'axios';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Plus, UserCheck, AlertTriangle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const ADD_NEW = '__add_new__';

/**
 * Reusable Dentist picker.
 *
 * Props:
 *  - dentists:            full list of dentist objects from /api/practice/dentists
 *  - selectedId:          dentist.id of current selection (preferred)
 *  - selectedName:        fallback string match by full name (legacy callers)
 *  - onChange({id, name, dentist}): fires whenever user picks
 *  - onDentistAdded(dentist):       fires after a new dentist is added successfully
 *  - error:               error message string to render below
 *  - placeholder:         placeholder text on the Select trigger
 *  - testIdPrefix:        prefix for data-testid attributes (e.g. "compliance" / "consent" / "procedure")
 *  - showCellWarning:     bool — if true, surfaces an amber banner when the
 *                         picked dentist has no `cellPhone`. Defaults to true.
 */
const DentistPicker = ({
  dentists = [],
  selectedId,
  selectedName,
  onChange,
  onDentistAdded,
  error,
  placeholder = 'Select a dentist…',
  testIdPrefix = 'dentist',
  showCellWarning = true,
}) => {
  const { toast } = useToast();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // When set, the dialog is in "edit existing dentist" mode (PUT) instead of
  // "create new" (POST). Used to let the user fill in a missing cell phone
  // straight from the inline cell-warning.
  const [editingDentistId, setEditingDentistId] = useState(null);
  const [draft, setDraft] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    cellPhone: '',
  });
  const [draftErrors, setDraftErrors] = useState({});

  const isEditing = !!editingDentistId;

  const openAddDialog = () => {
    setEditingDentistId(null);
    setDraft({ firstName: '', lastName: '', email: '', phone: '', cellPhone: '' });
    setDraftErrors({});
    setAddOpen(true);
  };

  // Open the dialog pre-populated with an existing dentist (for editing missing fields)
  const openEditDialog = (d) => {
    setEditingDentistId(d.id);
    setDraft({
      firstName: d.firstName || '',
      lastName: d.lastName || '',
      email: d.email || '',
      phone: d.phone || '',
      cellPhone: d.cellPhone || '',
    });
    setDraftErrors({});
    setAddOpen(true);
  };

  // Resolve currently selected dentist by id first, then by full-name fallback.
  const selected = (() => {
    if (selectedId) return dentists.find((d) => d.id === selectedId) || null;
    if (selectedName) {
      const lc = selectedName.trim().toLowerCase();
      return (
        dentists.find(
          (d) => `${d.firstName} ${d.lastName}`.trim().toLowerCase() === lc
        ) || null
      );
    }
    return null;
  })();

  const selectValue = selected?.id || '';

  const handleSelect = (val) => {
    if (val === ADD_NEW) {
      openAddDialog();
      return;
    }
    const d = dentists.find((x) => x.id === val);
    if (!d) return;
    onChange?.({
      id: d.id,
      name: `Dr. ${d.firstName} ${d.lastName}`.trim(),
      dentist: d,
    });
  };

  const handleSaveNew = async () => {
    const errs = {};
    if (!draft.firstName.trim()) errs.firstName = 'Required';
    if (!draft.lastName.trim()) errs.lastName = 'Required';
    if (!draft.email.trim()) errs.email = 'Required';
    setDraftErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSaving(true);
    try {
      const auth = { headers: { Authorization: `Bearer ${localStorage.getItem('dentalToken')}` } };
      const payload = {
        firstName: draft.firstName.trim(),
        lastName: draft.lastName.trim(),
        email: draft.email.trim(),
        phone: draft.phone.trim() || null,
        cellPhone: draft.cellPhone.trim() || null,
      };

      let dentistObj;
      if (isEditing) {
        // Editing an existing dentist (e.g. filling in their missing cell phone)
        await axios.put(
          `${backendUrl}/api/practice/dentists/${editingDentistId}`,
          payload,
          auth,
        );
        dentistObj = { id: editingDentistId, ...payload };
        onDentistAdded?.(dentistObj); // parent will replace-or-append in its sorted list
        toast({
          title: 'Dentist updated',
          description: `Dr. ${dentistObj.firstName} ${dentistObj.lastName} — saved.`,
        });
      } else {
        const res = await axios.post(`${backendUrl}/api/practice/dentists`, payload, auth);
        const created = res.data?.data || res.data;
        const id = created?.id || created?.dentistId;
        if (!id) throw new Error('Server did not return a dentist id.');
        dentistObj = { id, ...payload };
        onDentistAdded?.(dentistObj);
        toast({
          title: 'Dentist added',
          description: `Dr. ${dentistObj.firstName} ${dentistObj.lastName} is now available.`,
        });
      }

      // Auto-select the saved dentist so the form picks up the new cellPhone immediately
      onChange?.({
        id: dentistObj.id,
        name: `Dr. ${dentistObj.firstName} ${dentistObj.lastName}`.trim(),
        dentist: dentistObj,
      });

      setAddOpen(false);
      setEditingDentistId(null);
      setDraft({ firstName: '', lastName: '', email: '', phone: '', cellPhone: '' });
      setDraftErrors({});
    } catch (err) {
      console.error('Save dentist failed:', err);
      toast({
        title: isEditing ? 'Could not update dentist' : 'Could not add dentist',
        description: err.response?.data?.detail || err.message || 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // ---- Empty state ----
  if (dentists.length === 0) {
    return (
      <>
        <div
          className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2"
          data-testid={`${testIdPrefix}-no-dentists-card`}
        >
          <p className="text-sm text-amber-900">No dentists are set up in this practice yet.</p>
          <Button
            type="button"
            onClick={openAddDialog}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            size="sm"
            data-testid={`${testIdPrefix}-add-dentist-btn`}
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Add a dentist now
          </Button>
        </div>
        {renderDialog()}
      </>
    );
  }

  return (
    <>
      <Select value={selectValue} onValueChange={handleSelect}>
        <SelectTrigger
          className={error ? 'border-red-500' : ''}
          data-testid={`${testIdPrefix}-dentist-select`}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {dentists.map((d) => (
            <SelectItem
              key={d.id}
              value={d.id}
              data-testid={`${testIdPrefix}-dentist-option-${d.id}`}
            >
              Dr. {d.firstName} {d.lastName}
              {d.specialties?.length > 0 ? (
                <span className="text-gray-400 ml-2 text-xs">
                  · {d.specialties.join(', ')}
                </span>
              ) : null}
            </SelectItem>
          ))}
          <SelectItem
            value={ADD_NEW}
            className="text-blue-600 font-medium"
            data-testid={`${testIdPrefix}-dentist-add-new-option`}
          >
            <span className="inline-flex items-center gap-1.5">
              <Plus className="h-3.5 w-3.5" /> Add a new dentist…
            </span>
          </SelectItem>
        </SelectContent>
      </Select>

      {showCellWarning && selected && !selected.cellPhone && (
        <button
          type="button"
          onClick={() => openEditDialog(selected)}
          className="mt-2 w-full flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 hover:bg-amber-100 hover:border-amber-400 p-2.5 text-left transition-colors"
          data-testid={`${testIdPrefix}-dentist-cell-warning`}
        >
          <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-xs text-amber-900 leading-relaxed">
              <strong>Heads up:</strong> Dr. {selected.firstName} {selected.lastName} has no
              cell phone on file. Post-treatment patient replies and unsigned-form alerts
              won't reach this dentist by SMS.
            </p>
            <p className="text-xs font-semibold text-amber-800 mt-1 inline-flex items-center gap-1">
              Click here to add a cell phone now <Plus className="h-3 w-3" />
            </p>
          </div>
        </button>
      )}

      {renderDialog()}
    </>
  );

  function renderDialog() {
    return (
      <Dialog
        open={addOpen}
        onOpenChange={(o) => {
          setAddOpen(o);
          if (!o) {
            setDraftErrors({});
            setEditingDentistId(null);
          }
        }}
      >
        <DialogContent
          className="sm:max-w-md"
          data-testid={`${testIdPrefix}-add-dentist-dialog`}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-blue-600" />
              {isEditing ? 'Edit Dentist' : 'Add a Dentist'}
            </DialogTitle>
            <p className="text-sm text-gray-500 mt-1">
              {isEditing
                ? "Add or update this dentist's contact info. The cell phone receives SMS alerts when patients reply."
                : "Quickly add a new provider. They'll appear in the practice's Manage Dentists list afterward."}
            </p>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor={`${testIdPrefix}-add-first`}>First name *</Label>
                <Input
                  id={`${testIdPrefix}-add-first`}
                  value={draft.firstName}
                  onChange={(e) => setDraft({ ...draft, firstName: e.target.value })}
                  placeholder="Jane"
                  className={draftErrors.firstName ? 'border-red-500' : ''}
                  data-testid={`${testIdPrefix}-add-dentist-first-name`}
                />
                {draftErrors.firstName && (
                  <p className="text-xs text-red-600 mt-1">{draftErrors.firstName}</p>
                )}
              </div>
              <div>
                <Label htmlFor={`${testIdPrefix}-add-last`}>Last name *</Label>
                <Input
                  id={`${testIdPrefix}-add-last`}
                  value={draft.lastName}
                  onChange={(e) => setDraft({ ...draft, lastName: e.target.value })}
                  placeholder="Smith"
                  className={draftErrors.lastName ? 'border-red-500' : ''}
                  data-testid={`${testIdPrefix}-add-dentist-last-name`}
                />
                {draftErrors.lastName && (
                  <p className="text-xs text-red-600 mt-1">{draftErrors.lastName}</p>
                )}
              </div>
            </div>
            <div>
              <Label htmlFor={`${testIdPrefix}-add-email`}>Email *</Label>
              <Input
                id={`${testIdPrefix}-add-email`}
                type="email"
                value={draft.email}
                onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                placeholder="dentist@yourpractice.com"
                className={draftErrors.email ? 'border-red-500' : ''}
                data-testid={`${testIdPrefix}-add-dentist-email`}
              />
              {draftErrors.email && (
                <p className="text-xs text-red-600 mt-1">{draftErrors.email}</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor={`${testIdPrefix}-add-phone`}>Office phone</Label>
                <Input
                  id={`${testIdPrefix}-add-phone`}
                  value={draft.phone}
                  onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  data-testid={`${testIdPrefix}-add-dentist-phone`}
                />
              </div>
              <div>
                <Label htmlFor={`${testIdPrefix}-add-cell`}>
                  Cell phone {isEditing && !draft.cellPhone ? <span className="text-amber-700 ml-1">(missing)</span> : null}
                </Label>
                <Input
                  id={`${testIdPrefix}-add-cell`}
                  value={draft.cellPhone}
                  onChange={(e) => setDraft({ ...draft, cellPhone: e.target.value })}
                  placeholder="(555) 555-1234"
                  data-testid={`${testIdPrefix}-add-dentist-cell`}
                  autoFocus={isEditing && !draft.cellPhone}
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 -mt-1">
              Cell phone receives SMS alerts when patients reply to a follow-up message.
            </p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setAddOpen(false)}
              disabled={saving}
              data-testid={`${testIdPrefix}-add-dentist-cancel`}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSaveNew}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid={`${testIdPrefix}-add-dentist-save`}
            >
              {saving ? 'Saving…' : isEditing ? 'Save Changes' : 'Save Dentist'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
};

export default DentistPicker;
